import { useEffect, useRef } from 'react'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useAuth } from 'hooks/UseAuth'

export interface AudioSaveCompleteEvent {
  op: 'audio_save_complete'
  version: number
  success: true
}

export interface AudioSaveFailedEvent {
  op: 'audio_save_failed'
  error: string
  success: false
}

export interface VoiceoverRegenerateCompleteEvent {
  op: 'voiceover_regenerate_complete'
  version: number
  success: true
}

export interface VoiceoverRegenerateFailedEvent {
  op: 'voiceover_regenerate_failed'
  error: string
  success: false
}

type VideoTransformEvent =
  | AudioSaveCompleteEvent
  | AudioSaveFailedEvent
  | VoiceoverRegenerateCompleteEvent
  | VoiceoverRegenerateFailedEvent

interface UseVideoTransformChannelOptions {
  almanacFileKey: string
  onAudioSaveComplete: () => void
  onAudioSaveFailed: (error: string) => void
  onVoiceoverRegenerateComplete?: () => void
  onVoiceoverRegenerateFailed?: (error: string) => void
  enabled?: boolean
}

export const useVideoTransformChannel = ({
  almanacFileKey,
  onAudioSaveComplete,
  onAudioSaveFailed,
  onVoiceoverRegenerateComplete,
  onVoiceoverRegenerateFailed,
  enabled = true,
}: UseVideoTransformChannelOptions) => {
  const auth = useAuth()
  const websocketConnection = useWebsocketConnection()

  // Use refs to avoid recreating subscriptions when callbacks change
  const onCompleteRef = useRef(onAudioSaveComplete)
  const onFailedRef = useRef(onAudioSaveFailed)
  const onVoiceoverCompleteRef = useRef(onVoiceoverRegenerateComplete)
  const onVoiceoverFailedRef = useRef(onVoiceoverRegenerateFailed)

  useEffect(() => {
    onCompleteRef.current = onAudioSaveComplete
    onFailedRef.current = onAudioSaveFailed
    onVoiceoverCompleteRef.current = onVoiceoverRegenerateComplete
    onVoiceoverFailedRef.current = onVoiceoverRegenerateFailed
  }, [onAudioSaveComplete, onAudioSaveFailed, onVoiceoverRegenerateComplete, onVoiceoverRegenerateFailed])

  useEffect(() => {
    if (!auth || !websocketConnection || !enabled || !almanacFileKey) return

    const subscription = websocketConnection.subscriptions.create(
      {
        channel: 'VideoTransformChannel',
        key: almanacFileKey,
      },
      {
        received(data: VideoTransformEvent) {
          if (data.op === 'audio_save_complete') {
            onCompleteRef.current()
          } else if (data.op === 'audio_save_failed') {
            onFailedRef.current(data.error)
          } else if (data.op === 'voiceover_regenerate_complete') {
            onVoiceoverCompleteRef.current?.()
          } else if (data.op === 'voiceover_regenerate_failed') {
            onVoiceoverFailedRef.current?.(data.error)
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [auth, websocketConnection, almanacFileKey, enabled])
}
